import { useState } from 'react';
import type { MouseEventHandler } from 'react';
import type { User } from '@/users/src/types/user';
import { __ } from '@/hub-shared/utils/i18n';
import { LoadingState } from '@tpct/admin-ui';
import ProfileAvatar from '@/hub-shared/components/ProfileAvatar';
import FeatureGateTooltip from '@/hub-shared/components/FeatureGateTooltip';
import { getAdvancedUserManagementGate } from '@/hub-shared/gates/featureGateDecisions';
import UserCompaniesTable from '@/users/src/components/UserCompaniesTable';
import UserOngoingMissionsSection from '@/users/src/components/UserOngoingMissionsSection';
import type { UserOngoingMissionReportApiRow } from '@/users/src/utils/mapUserOngoingMissionReports';
import ChangeUserRoleModal from '@/users/src/components/ChangeUserRoleModal';
import { getRoleDisplayName } from '@/users/src/utils/formatting';
import { isCabinetOwnerUser } from '@/users/src/utils/userDeleteGuards';

type UserPageProps = {
  user: User | null;
  status: 'loading' | 'ready' | 'error';
  errorMessage?: string;
  bootstrapKey?: string;
  currentUserId?: number | null;
  canManageUsers?: boolean;
  canManageUserAccess?: boolean;
  canUseAdvancedUserManagement?: boolean;
  subscriptionTierUrl?: string;
  onCancel: () => void;
  onUserRefresh?: (user: User) => void;
  onRefreshUserDetail?: () => void | Promise<void>;
};

const UserPage = ({
  user,
  status,
  errorMessage,
  bootstrapKey = 'AccountingHubUsersAdmin',
  currentUserId = null,
  canManageUsers = true,
  canManageUserAccess = true,
  canUseAdvancedUserManagement = true,
  subscriptionTierUrl,
  onCancel,
  onUserRefresh,
  onRefreshUserDetail,
}: UserPageProps) => {
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const changeRoleGate = getAdvancedUserManagementGate({
    advancedUserManagement: true,
    manageUserAccess: canManageUserAccess,
    requireManageUserAccess: true,
  });
  const advancedUserManagementGate = getAdvancedUserManagementGate({
    advancedUserManagement: canUseAdvancedUserManagement,
    subscriptionTierUrl,
  });

  if (status === 'loading') {
    return (
      <LoadingState
        columns={[__('Client'), __('Actions')]}
        className="user-page-loading"
      />
    );
  }

  if (status === 'error' || user === null) {
    return (
      <section
        className="container-style user-page"
        aria-label={__('Erreur chargement utilisateur')}
      >
        <p className="notice notice-error" role="alert">
          {errorMessage ?? __("Impossible de charger l'utilisateur.")}
        </p>
        <button type="button" className="btn-secondary" onClick={onCancel}>
          {__('Retour à la liste')}
        </button>
      </section>
    );
  }

  const fullNameFromParts = [user.last_name, user.first_name]
    .map(s => s.trim())
    .filter(s => s.length > 0)
    .join(' ');
  const displayName = user.name !== '' ? user.name : fullNameFromParts;
  const roleLabel = getRoleDisplayName(user.role);
  const showChangeRole =
    canManageUsers
    && !isCabinetOwnerUser(user)
    && (currentUserId === null || currentUserId === undefined || user.id !== currentUserId);

  const handleOpenRoleModal: MouseEventHandler<HTMLButtonElement> = () => {
    setRoleModalOpen(true);
  };

  return (
    <section className="user-page" aria-label={__('Dossiers attribués')}>
      <div className="container-style user-page-header">
        <ProfileAvatar
          src={user.avatar_url}
          alt={displayName}
          className="user-page-header__avatar"
        />
        <div className="user-page-header__text">
          <h3 className="user-page-header__name">{displayName}</h3>
          <div className="user-page-header__role-row">
            <p className="user-page-header__role">{roleLabel}</p>
            {showChangeRole ? (
              <FeatureGateTooltip decision={changeRoleGate}>
                <button
                  type="button"
                  className="btn-tertiary --small"
                  onClick={handleOpenRoleModal}
                  aria-label={__('Changer le rôle')}
                  disabled={!changeRoleGate.allowed}
                >
                  {__('Changer le rôle')}
                </button>
              </FeatureGateTooltip>
            ) : null}
          </div>
        </div>
      </div>
      <UserCompaniesTable
        user={user}
        bootstrapKey={bootstrapKey}
        canManageUsers={canManageUsers}
        canUseAdvancedUserManagement={canUseAdvancedUserManagement}
        advancedUserManagementGate={advancedUserManagementGate}
        onUserRefresh={onUserRefresh}
      />

      <UserOngoingMissionsSection
        reports={user.ongoing_mission_reports as UserOngoingMissionReportApiRow[] | undefined}
        bootstrapKey={bootstrapKey}
        onRefresh={() => {
          void onRefreshUserDetail?.();
        }}
      />

      {roleModalOpen ? (
        <ChangeUserRoleModal
          open={roleModalOpen}
          user={user}
          bootstrapKey={bootstrapKey}
          canManageUsers={canManageUsers}
          onClose={() => {
            setRoleModalOpen(false);
          }}
          onSuccess={(updated) => {
            onUserRefresh?.(updated);
          }}
        />
      ) : null}
    </section>
  );
};

export default UserPage;
